import { useCallback, useState } from "react";

import { differenceInDays, fromUnixTime, isAfter, isToday } from "date-fns";
import pluralize from "pluralize";

import { SetAdvisorPreferences } from "data/libs/advisor";

import { fromGlobalId } from "scenes/Hubly/components/Workspace/api/helper";

import { createResolverFactory, useQuery } from ".";

const updateBannerSnoozePreference = (userId, snoozeDuration) => {
  return SetAdvisorPreferences(fromGlobalId(userId), {
    key: "snooze_insights",
    value: {
      lastDismissal: Math.round(new Date().getTime() / 1000),
      days: snoozeDuration,
    },
  });
};

function useInsightsTrial(user) {
  const queryFactory = useCallback(() => {
    const query = `
    query getInsightsTrialStatus {
        insightsTrialStatus {
          isTrial
          trialEndDate
          shouldSnoozeReminder
        }
      }`;

    const resolver = ({ data: { insightsTrialStatus } }) => {
      return {
        ...insightsTrialStatus,
        trialEndDate: insightsTrialStatus?.trialEndDate
          ? fromUnixTime(insightsTrialStatus.trialEndDate)
          : null,
      };
    };

    return { query: query, resolver: createResolverFactory(resolver) };
  }, []);

  const [visible, setVisible] = useState(true);
  const {
    loading,
    data: { isTrial, trialEndDate, shouldSnoozeReminder } = {},
    error,
  } = useQuery(queryFactory);

  const getExpirationMessage = useCallback((expirationDate) => {
    const daysRemaining = differenceInDays(expirationDate, new Date());
    if (isToday(expirationDate) || daysRemaining === 0)
      return "Your Insights trial will expire today.";

    if (isAfter(expirationDate, new Date())) {
      return `Your Insights trial will expire in ${pluralize(
        "day",
        daysRemaining,
        true
      )}.`;
    }

    return "Your Insights trial has already expired.";
  }, []);

  return {
    showBanner:
      !error && !loading && visible && isTrial && !shouldSnoozeReminder,
    expirationMessage: getExpirationMessage(trialEndDate),
    expirationDate: trialEndDate,
    snoozeBanner: ({ days } = {}) => {
      setVisible(false);

      if (days) updateBannerSnoozePreference(user.id, days);
    },
  };
}

export default useInsightsTrial;
